import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, Play, Clock, ExternalLink, Star, Rocket, MapPin, Mail, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { getPodcastEpisodes, subscribeToPodcast } from '../services/api';

const APPLE_PODCAST_URL = "https://podcasts.apple.com/us/podcast/the-founder-initiative/id1557368060";
const INITIAL_LOAD = 25;
const LOAD_MORE_COUNT = 25;

const Podcast = () => {
  const navigate = useNavigate();
  const [podcastData, setPodcastData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [visibleCount, setVisibleCount] = useState(INITIAL_LOAD);
  const [allEpisodesLoaded, setAllEpisodesLoaded] = useState(false);
  const loadMoreRef = useRef(null);

  // Initial fast load - just first batch
  useEffect(() => {
    loadInitialPodcast();
  }, []);

  // Load remaining episodes in background after initial render
  useEffect(() => {
    if (podcastData && !allEpisodesLoaded) {
      loadAllEpisodes();
    }
  }, [podcastData, allEpisodesLoaded]);

  const loadInitialPodcast = async () => {
    try {
      const data = await getPodcastEpisodes(INITIAL_LOAD);
      setPodcastData(data);
    } catch (error) {
      console.error('Error loading podcast:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllEpisodes = async () => {
    try {
      // Load all episodes in background
      const data = await getPodcastEpisodes(300);
      setPodcastData(data);
      setAllEpisodesLoaded(true);
    } catch (error) {
      console.error('Error loading all episodes:', error);
    }
  };

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore) {
          loadMoreEpisodes();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [loadingMore, visibleCount, podcastData]);

  const loadMoreEpisodes = useCallback(() => {
    const totalEpisodes = podcastData?.episodes?.length || 0;
    if (visibleCount >= totalEpisodes) return;
    
    setLoadingMore(true);
    // Small delay to show loading state
    setTimeout(() => {
      setVisibleCount(prev => Math.min(prev + LOAD_MORE_COUNT, totalEpisodes));
      setLoadingMore(false);
    }, 300);
  }, [visibleCount, podcastData]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribing(true);
    try {
      await subscribeToPodcast(email);
      setSubscribeStatus('success');
      setEmail('');
    } catch (error) {
      setSubscribeStatus('error');
    } finally {
      setSubscribing(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Separate featured and YC episodes
  const featuredEpisodes = podcastData?.episodes?.filter(ep => ep.featured) || [];
  const ycEpisodes = podcastData?.episodes?.filter(ep => ep.is_yc_interview) || [];
  const allEpisodes = podcastData?.episodes?.filter(ep => !ep.featured) || [];
  
  // Filter based on active tab
  const filteredEpisodes = activeTab === 'yc' ? ycEpisodes : allEpisodes;
  
  // Apply visible count limit
  const displayedEpisodes = filteredEpisodes.slice(0, visibleCount);
  const hasMoreEpisodes = filteredEpisodes.length > visibleCount;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header - Same as main page */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <a href="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-white">The Founder Initiative</span>
          </a>
          <nav className="hidden md:flex gap-8 text-sm font-medium text-slate-400">
            <a href="/buyers" className="hover:text-white transition-colors">Buyers</a>
            <a href="/podcast" className="text-white transition-colors">Podcast</a>
            <a href="/sponsors" className="hover:text-white transition-colors">Sponsors</a>
          </nav>
          <a 
            href="https://app.thefounderinitiative.com" 
            className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0 h-10 px-4 py-2"
          >
            Login
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 rounded-full px-4 py-2">
              <Mic className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-purple-400 font-medium">The Founder Initiative Podcast</span>
            </div>
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 py-2">
              <MapPin className="w-4 h-4 text-orange-400" />
              <span className="text-sm text-orange-400 font-medium">San Francisco, CA</span>
            </div>
          </div>
          
          {podcastData?.podcast?.image && (
            <div className="mb-8">
              <img 
                src={podcastData.podcast.image} 
                alt="Podcast Cover"
                className="w-48 h-48 mx-auto rounded-2xl shadow-2xl shadow-purple-500/20"
              />
            </div>
          )}
          
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Stories from{' '}
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Top Founders & VCs
            </span>
          </h1>
          
          <p className="text-xl text-slate-400 leading-relaxed max-w-3xl mx-auto mb-8">
            Helping founders hit PMF, get funded & drive durable growth. 
            Hear firsthand stories from successful founders and investors.
          </p>

          <div className="flex justify-center gap-4">
            <a 
              href="https://open.spotify.com/show/0GMQ4jSVcRvfPVpRYrx4s3" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 px-6 py-3 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.586 14.424c-.161.27-.514.354-.788.193-2.156-1.317-4.872-1.614-8.069-.884-.308.07-.617-.12-.687-.427-.07-.308.12-.616.427-.687 3.497-.799 6.497-.455 8.924 1.018.274.161.358.514.193.787zm1.224-2.723c-.2.331-.633.435-.965.235-2.468-1.517-6.228-1.957-9.144-1.07-.378.115-.776-.097-.891-.474-.115-.378.097-.777.474-.892 3.33-1.013 7.467-.522 10.291 1.235.331.2.435.634.235.966zm.106-2.84C14.584 8.997 8.665 8.757 5.318 9.754c-.452.134-.928-.122-1.062-.574-.134-.452.122-.929.574-1.063 3.842-1.144 10.227-.922 14.262 1.238.419.243.56.778.316 1.197-.243.42-.777.56-1.197.317z"/>
              </svg>
              Listen on Spotify
            </a>
            <a 
              href="https://podcasts.apple.com/us/podcast/the-founder-initiative/id1557368060" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 px-6 py-3 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18c-4.418 0-8-3.582-8-8s3.582-8 8-8 8 3.582 8 8-3.582 8-8 8zm-1-13v6l5.2 3.1-.7 1.2-5.5-3.3V7h1z"/>
              </svg>
              Apple Podcasts
            </a>
          </div>

          {/* Email Subscribe */}
          <div className="mt-10 max-w-md mx-auto">
            <p className="text-slate-400 text-sm mb-3 text-center">
              Get notified when new episodes drop
            </p>
            {subscribeStatus === 'success' ? (
              <div className="flex items-center justify-center gap-2 bg-green-500/10 border border-green-500/30 rounded-lg px-6 py-3 text-green-400">
                <Check className="w-5 h-5" />
                <span>You're subscribed! We'll notify you of new episodes.</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full pl-11 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={subscribing}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6"
                >
                  {subscribing ? 'Subscribing...' : 'Subscribe'}
                </Button>
              </form>
            )}
            {subscribeStatus === 'error' && (
              <p className="text-red-400 text-sm mt-2 text-center">
                Something went wrong. Please try again.
              </p>
            )}
          </div>
        </div>
      </section>

      {loading ? (
        <div className="text-center py-20">
          <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-400">Loading episodes...</p>
        </div>
      ) : (
        <>
          {/* Featured Episodes */}
          {featuredEpisodes.length > 0 && (
            <section className="py-16 px-6 bg-slate-900/30 border-y border-slate-800">
              <div className="max-w-6xl mx-auto">
                <div className="flex items-center gap-2 mb-8">
                  <Star className="w-5 h-5 text-yellow-400" />
                  <h2 className="text-2xl font-bold text-white">Featured Episodes</h2>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  {featuredEpisodes.map((episode) => (
                    <Card key={episode.guid} className="bg-slate-900/80 border-yellow-500/30 hover:border-yellow-500/50 transition-all">
                      <CardContent className="p-6">
                        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 mb-3">
                          <Star className="w-3 h-3 mr-1" /> Featured
                        </Badge>
                        <h3 className="text-lg font-bold text-white mb-2 line-clamp-2">
                          {episode.title}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-slate-400 mb-4">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {episode.duration}
                          </span>
                          <span>{formatDate(episode.pubDate)}</span>
                        </div>
                        <div className="flex gap-2">
                          <a
                            href={episode.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg transition-colors text-sm"
                          >
                            <Play className="w-4 h-4" /> Listen Now
                          </a>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Recent Episodes with Tabs */}
          <section className="py-16 px-6">
            <div className="max-w-6xl mx-auto">
              {/* Tabs */}
              <div className="flex items-center gap-2 mb-8">
                <button
                  onClick={() => setActiveTab('all')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    activeTab === 'all'
                      ? 'bg-purple-600 text-white'
                      : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  All Episodes
                </button>
                <button
                  onClick={() => setActiveTab('yc')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    activeTab === 'yc'
                      ? 'bg-orange-500 text-white'
                      : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <span className="font-bold">Y</span> YC Founders
                  {ycEpisodes.length > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      activeTab === 'yc' ? 'bg-orange-600' : 'bg-slate-700'
                    }`}>
                      {ycEpisodes.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Episode List */}
              {displayedEpisodes.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  {activeTab === 'yc' ? (
                    <p>No YC founder interviews tagged yet. Check back soon!</p>
                  ) : (
                    <p>No episodes found.</p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {displayedEpisodes.map((episode, index) => (
                    <Card key={episode.guid} className={`bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all ${
                      episode.is_yc_interview ? 'border-l-4 border-l-orange-500' : ''
                    }`}>
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
                            episode.is_yc_interview ? 'bg-orange-500/20 text-orange-400' : 'bg-slate-800 text-slate-500'
                          }`}>
                            {episode.is_yc_interview ? (
                              <span className="text-lg font-bold">Y</span>
                            ) : (
                              <span className="text-lg font-bold">#{episode.episode || (filteredEpisodes.length - index)}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-lg font-bold text-white line-clamp-2">
                                {episode.title}
                              </h3>
                              {episode.is_yc_interview && (
                                <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs">
                                  YC Founder
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-400">
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {episode.duration}
                              </span>
                              <span>{formatDate(episode.pubDate)}</span>
                            </div>
                          </div>
                          <a
                            href={episode.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-shrink-0 flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white py-2 px-4 rounded-lg transition-colors text-sm"
                          >
                            <Play className="w-4 h-4" /> Listen
                          </a>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {/* Load More / Infinite Scroll Trigger */}
                  {hasMoreEpisodes && (
                    <div ref={loadMoreRef} className="flex justify-center py-8">
                      {loadingMore ? (
                        <div className="flex items-center gap-2 text-slate-400">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Loading more episodes...</span>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          onClick={loadMoreEpisodes}
                          className="border-slate-700 text-slate-400 hover:text-white"
                        >
                          Load More Episodes ({filteredEpisodes.length - visibleCount} remaining)
                        </Button>
                      )}
                    </div>
                  )}
                  
                  {/* Episode count */}
                  {!hasMoreEpisodes && filteredEpisodes.length > 0 && (
                    <div className="text-center py-4 text-slate-500 text-sm">
                      Showing all {filteredEpisodes.length} episodes
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-800">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} The Founder Initiative. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Podcast;
